import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Check, Smartphone, QrCode, Users, BarChart3, 
  Mail, Video, Zap, ChevronRight, Star, Play, CreditCard,
  Scan, Eye, Globe, Share2, UserPlus, TrendingUp, Wifi,
  CheckCircle2, Sparkles, ArrowDown, Quote, Menu, X,
  ChevronDown, Wrench, Calendar, ShoppingCart
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import SignInModal from '../components/SignInModal';
import BookDemoModal from '../components/BookDemoModal';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import SEO from '../components/SEO';

// ============ ANIMATION STYLES ============
const animationStyles = `
  /* Smooth animation timing */
  :root {
    --ease-out-expo: cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-80px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(80px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-12px);
    }
  }
  
  @keyframes floatReverse {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  .animate-slide-in-left {
    animation: slideInLeft 0.9s var(--ease-out-expo) forwards;
  }
  
  .animate-slide-in-right {
    animation: slideInRight 0.9s var(--ease-out-expo) forwards;
  }
  
  .animate-float {
    animation: float 5s ease-in-out infinite;
  }
  
  .animate-float-reverse {
    animation: floatReverse 4.5s ease-in-out infinite;
  }
  
  .delay-100 { animation-delay: 0.1s; }
  .delay-200 { animation-delay: 0.2s; }
  .delay-300 { animation-delay: 0.3s; }
  .delay-400 { animation-delay: 0.4s; }
  .delay-500 { animation-delay: 0.5s; }
  .delay-600 { animation-delay: 0.6s; }
  
  /* Smooth hover effects */
  .card-hover {
    transition: transform 0.4s var(--ease-out-expo), box-shadow 0.4s var(--ease-out-expo);
  }
  
  .card-hover:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  }
`;

// Hook for intersection observer animations - REPEATABLE on scroll
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    }, { threshold: 0.15, rootMargin: '50px', ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

// Simplified Animated wrapper - only left/right animations
const AnimatedSection = ({ children, direction = "left", delay = "0", className = "" }) => {
  const [ref, isInView] = useInView();
  const animClass = direction === "left" ? "animate-slide-in-left" : "animate-slide-in-right";
  
  return (
    <div 
      ref={ref} 
      className={`${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateX(0)' : (direction === "left" ? 'translateX(-60px)' : 'translateX(60px)'),
        transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${parseInt(delay) * 0.001}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${parseInt(delay) * 0.001}s`
      }}
    >
      {children}
    </div>
  );
};

// Parallax floating card component
const FloatingCard = ({ children, className = "", delay = 0, direction = "up" }) => {
  const animClass = direction === "up" ? "animate-float" : "animate-float-reverse";
  return (
    <div 
      className={`${className} ${animClass}`} 
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Meeting Theme Preview Component with PiperCard backgrounds
const MeetingThemePreview = () => {
  return (
    <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden shadow-2xl aspect-video max-w-3xl mx-auto">
      {/* Grid of meeting participants - with PiperCard branded backgrounds */}
      <div className="absolute inset-3 grid grid-cols-3 gap-2">
        {/* Main speaker - larger with PiperCard background */}
        <div className="col-span-2 row-span-2 relative rounded-xl overflow-hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/dgq9g6dn_Raja%20M_background%20%283%29.png" 
            alt="Raja M - PiperCard Meeting Background"
            className="w-full h-full object-cover"
          />
          {/* Speaking indicator */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <div className="flex gap-0.5 items-end h-4">
              <span className="w-1 bg-green-400 rounded-full animate-pulse" style={{ height: '8px' }}></span>
              <span className="w-1 bg-green-400 rounded-full animate-pulse" style={{ height: '14px', animationDelay: '0.1s' }}></span>
              <span className="w-1 bg-green-400 rounded-full animate-pulse" style={{ height: '6px', animationDelay: '0.2s' }}></span>
              <span className="w-1 bg-green-400 rounded-full animate-pulse" style={{ height: '12px', animationDelay: '0.15s' }}></span>
              <span className="w-1 bg-green-400 rounded-full animate-pulse" style={{ height: '10px', animationDelay: '0.25s' }}></span>
            </div>
            <span className="text-white text-xs font-medium">Speaking</span>
          </div>
        </div>
        
        {/* Participant 1 - with PiperCard background */}
        <div className="relative rounded-lg overflow-hidden row-span-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/gyfzae7n_Raja%20M_background%20%282%29.png" 
            alt="Participant with PiperCard Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-1 left-1 bg-black/50 px-1.5 py-0.5 rounded text-white text-[10px]">
            Mike R.
          </div>
        </div>
      </div>
      
      {/* Meeting controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
        <div className="flex items-center justify-center gap-2">
          <button className="w-9 h-9 rounded-full bg-gray-700/80 hover:bg-gray-600 flex items-center justify-center transition-colors">
            <Video className="w-4 h-4 text-white" />
          </button>
          <button className="w-9 h-9 rounded-full bg-gray-700/80 hover:bg-gray-600 flex items-center justify-center transition-colors">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
          <button className="w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>
          <button className="w-9 h-9 rounded-full bg-gray-700/80 hover:bg-gray-600 flex items-center justify-center transition-colors">
            <Share2 className="w-4 h-4 text-white" />
          </button>
          <button className="w-9 h-9 rounded-full bg-gray-700/80 hover:bg-gray-600 flex items-center justify-center transition-colors">
            <Users className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
      
      {/* PiperCard branded background badge */}
      <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 px-2 py-1 rounded-lg flex items-center gap-1.5 shadow-lg">
        <img src="/logo.png" alt="PiperCard" className="w-4 h-4" />
        <span className="text-white text-xs font-semibold">PiperCard Backgrounds</span>
      </div>
    </div>
  );
};

// ============ COMPONENTS ============

// Header Component (matching Layout.js style)
const Header = ({ onCreateCard, onSignIn, onBookDemo }) => {
  const { user } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header className="bg-gray-900 border-b-2 border-gray-700 fixed top-0 left-0 right-0 z-[60] shadow-lg">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
          {/* Left side: Mobile Menu + Logo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 text-white transition-colors shadow-md border border-gray-700 flex-shrink-0"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
              <img src="/logo.png" alt="Piper Card Logo" className="h-7 sm:h-8 md:h-10 w-auto object-contain flex-shrink-0" />
              <span className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-amber-500 to-yellow-400 bg-clip-text text-transparent truncate">
                Piper Card
              </span>
            </Link>

            {/* Desktop Navigation */}
            {!user && (
              <div className="hidden lg:flex items-center gap-1 ml-4">
                {/* Tools Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium">
                      <Wrench className="w-4 h-4" />
                      <span>Tools</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64 bg-gray-900 border-gray-700 text-gray-100">
                    <DropdownMenuLabel className="text-amber-400 text-xs uppercase tracking-wider">
                      Free Marketing Tools
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800" onClick={() => navigate('/email-signature-generator')}>
                      <Mail className="w-4 h-4 mr-2 text-amber-400" />
                      <div>
                        <div className="font-medium">Email Signature Generator</div>
                        <div className="text-xs text-gray-400">Create professional email signatures</div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuItem className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800" onClick={() => navigate('/meeting-themes-generator')}>
                      <Video className="w-4 h-4 mr-2 text-blue-400" />
                      <div>
                        <div className="font-medium">Meeting Background Generator</div>
                        <div className="text-xs text-gray-400">Custom Zoom/Teams backgrounds</div>
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="bg-gray-700" />

                    <DropdownMenuItem className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800" onClick={() => navigate('/card-builder')}>
                      <CreditCard className="w-4 h-4 mr-2 text-amber-400" />
                      <div>
                        <div className="font-medium">Digital Business Card Maker</div>
                        <div className="text-xs text-gray-400">Create your digital card</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Other Nav Links */}
                <a href="#features" className="px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium">Features</a>
                <a href="#how-it-works" className="px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium">How It Works</a>
                <Link to="/pricing" className="px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium">Pricing</Link>
                <Link to="/shop" className="px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium">NFC Cards</Link>
              </div>
            )}
          </div>
          
          {/* Right side: Cart + Buttons */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Cart Button */}
            {itemCount > 0 && (
              <button
                onClick={() => navigate('/cart')}
                className="relative flex items-center gap-1.5 px-3 py-1.5 sm:py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all shadow-md font-semibold text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:inline">Cart</span>
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                  {itemCount}
                </span>
              </button>
            )}
            
            {user ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-3 sm:px-4 py-1.5 h-9 text-sm shadow-md"
              >
                Dashboard
              </Button>
            ) : (
              <>
                {/* Book Demo */}
                <Button
                  onClick={onBookDemo}
                  variant="outline"
                  className="hidden sm:flex items-center gap-1.5 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-3 py-1.5 h-9 text-sm"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden md:inline">Book Demo</span>
                </Button>
                
                {/* Create Card Button */}
                <Button
                  onClick={onCreateCard}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-3 sm:px-4 py-1.5 h-9 text-sm shadow-md"
                >
                  <Sparkles className="w-4 h-4 mr-1.5 hidden sm:inline" />
                  <span>Create Card</span>
                </Button>
                
                {/* Sign In Button */}
                <Button
                  onClick={onSignIn}
                  variant="outline"
                  className="flex items-center gap-1.5 border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-white px-3 py-1.5 h-9 text-sm font-medium"
                  data-testid="header-sign-in-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign In</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && !user && (
        <div className="lg:hidden bg-gray-800 border-t border-gray-700 py-4 px-4">
          <div className="space-y-2">
            <Link to="/email-signature-generator" className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">
              <Mail className="w-4 h-4 text-amber-400" />
              Email Signature Generator
            </Link>
            <Link to="/meeting-themes-generator" className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">
              <Video className="w-4 h-4 text-blue-400" />
              Meeting Background Generator
            </Link>
            <Link to="/card-builder" className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">
              <CreditCard className="w-4 h-4 text-amber-400" />
              Digital Business Card Maker
            </Link>
            <div className="border-t border-gray-700 my-2"></div>
            <Link to="/pricing" className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">Pricing</Link>
            <Link to="/shop" className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg">NFC Cards</Link>
          </div>
        </div>
      )}
    </header>
  );
};

// Primary CTA Button (Golden theme)
const PrimaryCTA = ({ onClick, children, size = 'default', className = '' }) => {
  const sizes = {
    small: 'px-5 h-11 text-sm',
    default: 'px-7 h-13 text-base',
    large: 'px-8 h-14 text-lg',
    xl: 'px-10 h-16 text-xl'
  };
  
  return (
    <Button 
      onClick={onClick}
      className={`bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 ${sizes[size]} ${className}`}
    >
      {children}
    </Button>
  );
};

// Secondary CTA Button
const SecondaryCTA = ({ onClick, children, size = 'default', className = '' }) => {
  const sizes = {
    small: 'px-5 h-11 text-sm',
    default: 'px-7 h-13 text-base',
    large: 'px-8 h-14 text-lg'
  };
  
  return (
    <Button 
      onClick={onClick}
      variant="outline"
      className={`border-2 border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-gray-900 font-semibold rounded-xl transition-all duration-300 ${sizes[size]} ${className}`}
    >
      {children}
    </Button>
  );
};

// Phone Mockup for Hero (Golden theme)
const HeroPhoneMockup = () => (
  <div className="relative">
    {/* Glow */}
    <div className="absolute -inset-8 bg-gradient-to-br from-amber-400/20 via-orange-400/20 to-yellow-400/20 rounded-[4rem] blur-3xl"></div>
    
    <div className="relative w-[280px] sm:w-[300px]">
      {/* Phone Frame */}
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-[2.5rem] p-2.5 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-gray-900 rounded-b-2xl z-20"></div>
        
        {/* Screen */}
        <div className="bg-white rounded-[2rem] overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 pt-10 pb-16 px-4 text-center relative">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
            </div>
            <div className="relative w-24 h-24 mx-auto rounded-full bg-white shadow-xl flex items-center justify-center mb-3 ring-4 ring-white/30">
              <span className="text-3xl font-bold bg-gradient-to-br from-amber-500 to-orange-500 bg-clip-text text-transparent">JD</span>
            </div>
            <h3 className="text-white font-bold text-xl relative">John Doe</h3>
            <p className="text-amber-100 text-sm relative">Product Manager at Acme Inc</p>
          </div>
          
          {/* Content */}
          <div className="px-4 -mt-8 pb-5 relative">
            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 ring-1 ring-gray-100">
              <div className="grid grid-cols-4 gap-3">
                {[
                  { icon: Mail, label: 'Email', color: 'bg-amber-100 text-amber-600' },
                  { icon: Smartphone, label: 'Call', color: 'bg-orange-100 text-orange-600' },
                  { icon: Globe, label: 'Website', color: 'bg-yellow-100 text-yellow-600' },
                  { icon: Share2, label: 'Share', color: 'bg-amber-100 text-amber-600' }
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center mb-1.5`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] text-gray-600 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* QR Code */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 text-center ring-1 ring-gray-100">
              <div className="w-24 h-24 mx-auto bg-white rounded-xl flex items-center justify-center shadow-sm mb-2">
                <QrCode className="w-16 h-16 text-gray-800" />
              </div>
              <p className="text-xs text-gray-500 font-medium">Scan to save contact</p>
            </div>
            
            {/* Save Button */}
            <button className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3.5 rounded-xl text-sm font-bold shadow-lg">
              Save Contact
            </button>
          </div>
        </div>
      </div>
    </div>
    
    {/* Floating Elements with parallax */}
    <FloatingCard direction="up" delay={0} className="absolute -right-6 sm:-right-12 top-20 bg-white rounded-xl px-4 py-3 shadow-xl ring-1 ring-gray-100">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center animate-pulse">
          <UserPlus className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">New Lead!</p>
          <p className="text-xs text-gray-500">Just now</p>
        </div>
      </div>
    </FloatingCard>
    
    <FloatingCard direction="down" delay={1.5} className="absolute -left-4 sm:-left-10 bottom-32 bg-white rounded-xl px-4 py-3 shadow-xl ring-1 ring-gray-100">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
          <Eye className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">156 views</p>
          <p className="text-xs text-gray-500">This week</p>
        </div>
      </div>
    </FloatingCard>
    
    {/* Additional floating element */}
    <FloatingCard direction="up" delay={2} className="absolute -right-2 sm:right-4 bottom-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg px-3 py-2 shadow-lg">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-white" />
        <span className="text-xs font-medium text-white">Instant Share</span>
      </div>
    </FloatingCard>
  </div>
);

// Feature Card (Golden accents)
const FeatureCard = ({ icon: Icon, title, description, iconBg, iconColor }) => (
  <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:border-amber-200 hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-300 group">
    <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl ${iconBg} flex items-center justify-center mb-3 sm:mb-5 group-hover:scale-110 transition-transform`}>
      <Icon className={`w-5 h-5 sm:w-7 sm:h-7 ${iconColor}`} />
    </div>
    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5 sm:mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

// Step Card
const StepCard = ({ number, icon: Icon, title, description }) => (
  <div className="text-center group">
    <div className="relative w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-5">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl sm:rounded-2xl rotate-6 opacity-20 group-hover:rotate-12 transition-transform"></div>
      <div className="relative w-full h-full bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xl shadow-amber-500/25">
        <Icon className="w-6 h-6 sm:w-9 sm:h-9 text-white" />
      </div>
      <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gray-900 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold ring-2 sm:ring-4 ring-white shadow-lg">
        {number}
      </div>
    </div>
    <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2">{title}</h3>
    <p className="text-gray-600 text-sm max-w-[240px] mx-auto">{description}</p>
  </div>
);

// Testimonial Card
const TestimonialCard = ({ quote, author, role, rating = 5 }) => (
  <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:border-amber-200 hover:shadow-xl transition-all duration-300">
    {/* Stars */}
    <div className="flex items-center gap-0.5 sm:gap-1 mb-3 sm:mb-4">
      {[...Array(rating)].map((_, i) => (
        <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-400 text-amber-400" />
      ))}
    </div>
    
    {/* Quote */}
    <div className="relative mb-4 sm:mb-6">
      <Quote className="absolute -top-1 -left-0.5 sm:-top-2 sm:-left-1 w-6 h-6 sm:w-8 sm:h-8 text-amber-100" />
      <p className="text-gray-700 leading-relaxed pl-4 sm:pl-6 text-sm sm:text-base">{quote}</p>
    </div>
    
    {/* Author */}
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-sm font-bold shadow-lg">
        {author.split(' ').map(n => n[0]).join('')}
      </div>
      <div>
        <p className="font-semibold text-gray-900 text-sm sm:text-base">{author}</p>
        <p className="text-xs sm:text-sm text-gray-500">{role}</p>
      </div>
    </div>
  </div>
);

// Product Demo Card with Image
const DemoCardWithImage = ({ image, title, description, contain = false }) => (
  <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 hover:border-amber-200 hover:shadow-xl transition-all duration-300 group">
    <div className={`${contain ? 'aspect-square' : 'aspect-[3/4]'} overflow-hidden bg-gray-50 flex items-center justify-center p-2`}>
      <img 
        src={image} 
        alt={title} 
        className={`${contain ? 'w-full h-full object-contain' : 'w-full h-full object-cover object-top'} group-hover:scale-105 transition-transform duration-500`}
      />
    </div>
    <div className="p-3 sm:p-5">
      <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-600 text-xs sm:text-sm line-clamp-2">{description}</p>
    </div>
  </div>
);

// Product Demo Card
const DemoCard = ({ icon: Icon, title, description, gradient }) => (
  <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-gray-100 hover:border-amber-200 hover:shadow-xl transition-all duration-300 text-center group">
    <div className={`w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-xl sm:rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
      <Icon className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
    </div>
    <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

// Section Header
const SectionHeader = ({ title, highlight, subtitle }) => (
  <div className="text-center mb-8 sm:mb-12 lg:mb-14 px-2">
    <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
      {title}{' '}
      {highlight && <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">{highlight}</span>}
    </h2>
    {subtitle && <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">{subtitle}</p>}
  </div>
);

// ============ MAIN COMPONENT ============

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  
  const handleCreateCard = () => navigate('/card-builder');

  const features = [
    { icon: CreditCard, title: "Digital Business Card", description: "Share your professional profile instantly with a beautiful, customizable digital card.", iconBg: "bg-amber-100", iconColor: "text-amber-600" },
    { icon: Scan, title: "NFC & QR Sharing", description: "Tap or scan to connect instantly. No app required for the person receiving your card.", iconBg: "bg-orange-100", iconColor: "text-orange-600" },
    { icon: Users, title: "Lead Capture CRM", description: "Automatically capture and manage new contacts. Never lose a lead again.", iconBg: "bg-yellow-100", iconColor: "text-yellow-600" },
    { icon: BarChart3, title: "Visitor Analytics", description: "Know who viewed your digital card, when they visited, and how they engaged.", iconBg: "bg-amber-100", iconColor: "text-amber-600" },
    { icon: Mail, title: "Email Signature Generator", description: "Turn every email you send into a networking opportunity with professional signatures.", iconBg: "bg-orange-100", iconColor: "text-orange-600" },
    { icon: Video, title: "Professional Meeting Themes", description: "Look more professional in virtual meetings with custom branded backgrounds.", iconBg: "bg-yellow-100", iconColor: "text-yellow-600" }
  ];

  const testimonials = [
    { quote: "Networking became effortless with PiperCard. I never print business cards anymore. It's saved me time and money while making a better impression.", author: "Alex Rivera", role: "Startup Founder" },
    { quote: "My sales team captures leads instantly during events. The analytics help us follow up at exactly the right time. Game changer for our pipeline.", author: "Sarah Mitchell", role: "Sales Manager" },
    { quote: "PiperCard makes my professional identity easy to share. Clients are always impressed by how modern and convenient it is.", author: "David Chen", role: "Business Consultant" }
  ];

  const steps = [
    { icon: UserPlus, title: "Create Your Profile", description: "Add your info, photo, social links, and customize your card design" },
    { icon: Share2, title: "Share Using QR or NFC", description: "Let people scan your QR code or tap your NFC smart card" },
    { icon: TrendingUp, title: "Capture & Track Leads", description: "See who viewed your card and capture their contact info" }
  ];

  return (
    <>
      <SEO 
        title="PiperCard - Digital Business Cards | Create Your Free Card Today"
        description="Create your free digital business card. Share with QR code or NFC, capture leads, track visitors. The modern way to network. No credit card required."
        keywords="digital business card, NFC card, QR code business card, networking, lead capture, professional card"
      />
      
      <div className="min-h-screen bg-white">
        {/* Header */}
        <Header 
          onCreateCard={handleCreateCard}
          onSignIn={() => setShowSignInModal(true)}
          onBookDemo={() => setShowDemoModal(true)}
        />

        {/* ===== 1. HERO SECTION ===== */}
        <section className="pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 lg:pb-28 bg-gradient-to-b from-amber-50/60 via-orange-50/30 to-white relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-10 w-80 h-80 bg-amber-200/40 rounded-full blur-3xl"></div>
            <div className="absolute top-40 right-10 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pt-4 sm:pt-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
              {/* Content - Always first on mobile */}
              <div className="text-center lg:text-left">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-amber-200 text-amber-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-8 shadow-sm">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                  The Future of Professional Networking
                </div>
                
                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-[1.15] mb-4 sm:mb-6">
                  Your Professional Identity in{' '}
                  <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 bg-clip-text text-transparent">One Smart Card</span>
                </h1>
                
                {/* Subheadline */}
                <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  Create your digital business card, share it with QR or NFC, capture leads, track visitors, and upgrade your networking.
                </p>
                
                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-4 sm:mb-6">
                  <PrimaryCTA onClick={handleCreateCard} size="large" className="w-full sm:w-auto text-sm sm:text-base" data-testid="hero-cta">
                    Create Your Free Digital Card
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </PrimaryCTA>
                  <SecondaryCTA onClick={() => setShowDemoModal(true)} size="default" className="w-full sm:w-auto">
                    <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-amber-600" />
                    Book Your Free Demo
                  </SecondaryCTA>
                </div>
                
                {/* Trust Indicator */}
                <p className="text-gray-500 text-xs sm:text-sm flex items-center justify-center lg:justify-start gap-2">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                  Free to start • No credit card required
                </p>
              </div>
              
              {/* Phone Mockup - Hidden on small mobile, shown on larger screens */}
              <div className="hidden sm:flex justify-center lg:justify-end mt-8 lg:mt-0">
                <HeroPhoneMockup />
              </div>
            </div>
            
            {/* Scroll Indicator */}
            <div className="hidden lg:flex justify-center mt-16">
              <a href="#product-demo" className="flex flex-col items-center gap-2 text-gray-400 hover:text-amber-600 transition-colors group">
                <span className="text-sm font-medium">See how it works</span>
                <ArrowDown className="w-5 h-5 animate-bounce" />
              </a>
            </div>
          </div>
        </section>

        {/* ===== 2. PRODUCT DEMO SECTION ===== */}
        <section id="product-demo" className="py-12 sm:py-16 lg:py-28 bg-white overflow-hidden">
          <style>{animationStyles}</style>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection direction="left" delay="100">
              <SectionHeader 
                title="See PiperCard in" 
                highlight="Action"
                subtitle="Real examples from our users. See how PiperCard transforms professional networking."
              />
            </AnimatedSection>
            
            {/* Main showcase - Lifestyle Image */}
            <AnimatedSection direction="right" delay="200" className="mb-10 sm:mb-16">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl group card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/dgq9g6dn_Raja%20M_background%20%283%29.png" 
                  alt="PiperCard Digital Business Card and QR Code"
                  className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
                  <p className="text-white text-sm sm:text-lg font-medium drop-shadow-lg">
                    Your digital card + QR code, always ready to share
                  </p>
                </div>
              </div>
            </AnimatedSection>
            
            {/* Feature Grid with Real Screenshots */}
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-5xl mx-auto mb-8 sm:mb-14">
              {/* Digital Card Profile */}
              <AnimatedSection direction="left" delay="100">
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-500 group h-full card-hover">
                  <div className="aspect-[3/4] overflow-hidden bg-gray-50 flex items-center justify-center p-2 relative">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/tgqfs2ev_Screenshot_20260311_020210_Chrome.jpg"
                      alt="Digital Card Profile" 
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-3 sm:p-5">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Digital Card Profile</h3>
                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2">Beautiful, customizable profile with your photo, contact info, and social links.</p>
                  </div>
                </div>
              </AnimatedSection>
              
              {/* QR Code */}
              <AnimatedSection direction="right" delay="200">
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-500 group h-full card-hover">
                  <div className="aspect-square overflow-hidden bg-gray-50 flex items-center justify-center p-6 sm:p-10 relative">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/v2hyjrdz_Screenshot_20260311_015206_Chrome.jpg"
                      alt="Branded QR Code" 
                      className="w-3/4 h-auto object-contain group-hover:scale-110 transition-all duration-700"
                    />
                  </div>
                  <div className="p-3 sm:p-5">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Branded QR Code</h3>
                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2">Your unique QR code with PiperCard branding. Perfect for print and presentations.</p>
                  </div>
                </div>
              </AnimatedSection>
              
              {/* Email Signature */}
              <AnimatedSection direction="left" delay="300">
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-500 group h-full card-hover">
                  <div className="aspect-square overflow-hidden bg-gray-50 flex items-center justify-center p-2 relative">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/lq7kw45g_Screenshot_20260311_015517_Chrome.jpg"
                      alt="Email Signature" 
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-3 sm:p-5">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">Email Signature</h3>
                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2">Professional signatures with photo & QR code. Works with Gmail & Outlook.</p>
                  </div>
                </div>
              </AnimatedSection>
            </div>
            
            {/* Live Demo Banner */}
            <AnimatedSection direction="right" delay="400">
              <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 relative overflow-hidden mb-8 sm:mb-14 group card-hover">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                </div>
                <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
                  <div className="text-center lg:text-left">
                    <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-3">
                      <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                      Live Demo
                    </div>
                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2">See a Real PiperCard in Action</h3>
                    <p className="text-gray-400 text-sm sm:text-base max-w-lg">Experience how a digital business card looks and works. Try scanning the QR code or tap the NFC card.</p>
                  </div>
                  <Link 
                    to="/bio/mraja" 
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-base shadow-lg shadow-amber-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/30 hover:scale-105 whitespace-nowrap"
                  >
                    <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                    View Live Demo
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
            
            {/* CTA */}
            <div className="text-center">
              <PrimaryCTA onClick={handleCreateCard} size="default" className="w-full sm:w-auto">
                Get Started Free
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </PrimaryCTA>
              <p className="text-gray-500 text-xs sm:text-sm mt-3">Takes less than 30 seconds to set up</p>
            </div>
          </div>
        </section>

        {/* ===== 3. FEATURES SECTION ===== */}
        <section id="features" className="py-12 sm:py-16 lg:py-28 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection direction="right" delay="100">
              <SectionHeader 
                title="Everything You Need for" 
                highlight="Modern Networking"
                subtitle="One platform with all the tools to create, share, and manage your professional identity."
              />
            </AnimatedSection>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-14">
              {features.map((feature, i) => (
                <AnimatedSection key={i} direction={i % 2 === 0 ? "left" : "right"} delay={`${(i % 3 + 1) * 100}`}>
                  <div className="card-hover h-full">
                    <FeatureCard {...feature} />
                  </div>
                </AnimatedSection>
              ))}
            </div>
            
            {/* Inline CTA Banner */}
            <AnimatedSection direction="left" delay="300">
              <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-center relative overflow-hidden group card-hover">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3 sm:mb-4 relative">Ready to upgrade your networking?</h3>
                <p className="text-amber-100 mb-4 sm:mb-6 relative max-w-lg mx-auto text-sm sm:text-base">Join professionals who have already made the switch to digital.</p>
                <SecondaryCTA onClick={handleCreateCard} size="default" className="bg-white hover:bg-gray-50 border-0 relative w-full sm:w-auto">
                  Create Your Free Card
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </SecondaryCTA>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ===== ANALYTICS SHOWCASE SECTION ===== */}
        <section className="py-12 sm:py-16 lg:py-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection direction="left" delay="100">
              <SectionHeader 
                title="Know Your" 
                highlight="Impact"
                subtitle="Track every view, click, and connection. Understand who's interested in your profile."
              />
            </AnimatedSection>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Profile Views */}
              <AnimatedSection direction="left" delay="100">
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 shadow-lg group card-hover">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/iy7tdl5s_Screenshot_20260311_015845_Chrome.jpg"
                    alt="Profile Views Analytics"
                    className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
              </AnimatedSection>
              
              {/* Link Clicks */}
              <AnimatedSection direction="right" delay="200">
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 shadow-lg group card-hover">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/4ps45eh5_Screenshot_20260311_015910_Chrome.jpg"
                    alt="Link Clicks Analytics"
                    className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
              </AnimatedSection>
              
              {/* Visitor Locations */}
              <AnimatedSection direction="left" delay="300">
                <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 shadow-lg md:col-span-2 lg:col-span-1 group card-hover">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_740f3ffe-3438-4cb0-9b22-f27497a2f603/artifacts/1yxxsg10_Screenshot_20260311_015917_Chrome.jpg"
                    alt="Visitor Locations Analytics"
                    className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
              </AnimatedSection>
            </div>
            
            <AnimatedSection direction="right" delay="400">
              <div className="text-center mt-8 sm:mt-12">
                <p className="text-gray-600 mb-4 text-sm sm:text-base">See exactly who's viewing your profile and where they're from</p>
                <PrimaryCTA onClick={handleCreateCard} size="default" className="w-full sm:w-auto">
                  Start Tracking Your Impact
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </PrimaryCTA>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ===== 4. HOW IT WORKS SECTION ===== */}
        <section id="how-it-works" className="py-12 sm:py-16 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader 
              title="Create Your Digital Card in" 
              highlight="30 Seconds"
              subtitle="It's easier than you think. Three simple steps to transform your networking."
            />
            
            <div className="grid grid-cols-3 gap-4 sm:gap-8 lg:gap-16 max-w-5xl mx-auto relative mb-8 sm:mb-14">
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-1 bg-gradient-to-r from-amber-200 via-orange-400 to-amber-200 rounded-full"></div>
              
              {steps.map((step, i) => (
                <StepCard key={i} number={i + 1} {...step} />
              ))}
            </div>
            
            {/* CTA */}
            <div className="text-center">
              <PrimaryCTA onClick={handleCreateCard} size="large" className="w-full sm:w-auto">
                Start Free Now
                <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
              </PrimaryCTA>
              <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                No credit card required • Free forever plan
              </p>
            </div>
          </div>
        </section>

        {/* ===== 5. TESTIMONIALS SECTION ===== */}
        <section id="testimonials" className="py-12 sm:py-16 lg:py-28 bg-gradient-to-b from-amber-50/50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader 
              title="Trusted by" 
              highlight="Modern Professionals"
              subtitle="Join thousands of entrepreneurs, sales pros, and teams using PiperCard."
            />
            
            {/* Testimonial Cards */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-14">
              {testimonials.map((testimonial, i) => (
                <TestimonialCard key={i} {...testimonial} />
              ))}
            </div>
            
            {/* CTA */}
            <div className="text-center">
              <PrimaryCTA onClick={handleCreateCard} size="default" className="w-full sm:w-auto">
                Get Started Free
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </PrimaryCTA>
            </div>
          </div>
        </section>

        {/* ===== 6. NFC CARD SECTION ===== */}
        <section className="py-12 sm:py-16 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
              {/* Content */}
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                  <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                  Premium Upgrade
                </div>
                
                <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Upgrade to{' '}
                  <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">NFC Smart Cards</span>
                </h2>
                
                <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  Tap your NFC card on any smartphone to instantly share your PiperCard profile. No app required. Works seamlessly with iPhone and Android.
                </p>
                
                <ul className="space-y-2 sm:space-y-4 mb-6 sm:mb-10">
                  {["Premium metal or PVC card options", "Custom branding and design", "Works with all modern smartphones", "Lifetime free profile updates"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button onClick={() => navigate('/shop')} className="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-6 sm:px-8 h-11 sm:h-14 text-sm sm:text-lg rounded-xl w-full sm:w-auto">
                    Order NFC Smart Card
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                  <SecondaryCTA onClick={handleCreateCard} size="default" className="w-full sm:w-auto">
                    Or Start Free
                  </SecondaryCTA>
                </div>
              </div>
              
              {/* NFC Card Visual - Hidden on mobile */}
              <div className="hidden sm:flex justify-center order-1 lg:order-2">
                <div className="relative">
                  <div className="absolute -inset-12 bg-gradient-to-br from-amber-400/10 via-orange-400/10 to-yellow-400/10 rounded-3xl blur-3xl"></div>
                  
                  <div className="relative">
                    {/* Back card */}
                    <div className="w-64 sm:w-80 h-40 sm:h-52 bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl shadow-xl transform rotate-6 absolute top-4 left-4"></div>
                    
                    {/* Front card */}
                    <div className="w-64 sm:w-80 h-40 sm:h-52 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 rounded-2xl shadow-2xl relative z-10 p-4 sm:p-6 flex flex-col justify-between overflow-hidden">
                      <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                      </div>
                      
                      <div className="flex items-center justify-between relative">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <img src="/logo.png" alt="PiperCard" className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl" />
                          <span className="text-white font-bold text-sm sm:text-lg">Piper Card</span>
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <Wifi className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                      </div>
                      
                      <div className="relative">
                        <p className="text-white/70 text-xs sm:text-sm">Tap to share</p>
                        <p className="text-white font-bold text-lg sm:text-2xl">Your Name Here</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* NFC Waves */}
                  <div className="absolute -right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-2">
                    {[5, 8, 12, 8, 5].map((w, i) => (
                      <div key={i} className="h-1.5 bg-amber-400 rounded-full animate-pulse" style={{ width: `${w * 3}px`, animationDelay: `${i * 0.1}s` }}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 7. BLOG / SEO SECTION ===== */}
        <section className="py-10 sm:py-14 lg:py-20 bg-gray-50 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">Resources & Guides</h2>
              <p className="text-gray-600 text-sm sm:text-base">Learn more about modern professional networking</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
              {[
                { icon: Mail, title: "Email Signature Guide", desc: "Create professional signatures that convert", link: "/email-signature-generator", color: "bg-amber-100 text-amber-600" },
                { icon: Wifi, title: "How NFC Cards Work", desc: "Learn the technology behind tap-to-share", link: "/how-nfc-works", color: "bg-orange-100 text-orange-600" },
                { icon: TrendingUp, title: "Networking Tips", desc: "Best practices for modern professionals", link: "/blog", color: "bg-yellow-100 text-yellow-600" }
              ].map((item, i) => (
                <Link key={i} to={item.link} className="group bg-white rounded-xl p-4 sm:p-6 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${item.color} flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base group-hover:text-amber-600 transition-colors">{item.title}</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ===== 8. FINAL CTA SECTION ===== */}
        <section className="py-14 sm:py-20 lg:py-32 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-yellow-400/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-5 sm:mb-8 backdrop-blur">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              Start networking smarter
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              Start Networking Smarter Today
            </h2>
            
            <p className="text-sm sm:text-base lg:text-xl text-amber-100 mb-6 sm:mb-10 max-w-2xl mx-auto">
              Create your free digital business card and start capturing more leads in less than 30 seconds.
            </p>
            
            <SecondaryCTA onClick={handleCreateCard} size="large" className="bg-white hover:bg-gray-50 border-0 text-gray-900 shadow-2xl px-6 sm:px-10 h-12 sm:h-16 text-base sm:text-xl w-full sm:w-auto" data-testid="final-cta">
              Create Your Free Digital Card
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2" />
            </SecondaryCTA>
            
            <p className="text-white/80 mt-4 sm:mt-6 text-xs sm:text-sm">
              No credit card required
            </p>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer className="bg-gray-900 text-gray-400 py-8 sm:py-12 border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
              {/* Brand */}
              <div className="col-span-2 sm:col-span-2 md:col-span-4 lg:col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-3 sm:mb-4">
                  <img src="/logo.png" alt="Piper Card" className="h-8 sm:h-10 w-auto" />
                  <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-amber-500 to-yellow-400 bg-clip-text text-transparent">Piper Card</span>
                </Link>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 leading-relaxed">
                  The modern way to network. Create, share, and manage your professional identity.
                </p>
                <PrimaryCTA onClick={handleCreateCard} size="small" className="w-full sm:w-auto text-xs sm:text-sm">
                  Create Free Card
                </PrimaryCTA>
              </div>
              
              {[
                { title: "Product", links: [{ label: "Features", to: "/features" }, { label: "Pricing", to: "/pricing" }, { label: "NFC Cards", to: "/shop" }] },
                { title: "Free Tools", links: [{ label: "Email Signature", to: "/email-signature-generator" }, { label: "Meeting Backgrounds", to: "/meeting-themes-generator" }, { label: "QR Generator", to: "/free-qr-code-generator" }] },
                { title: "Resources", links: [{ label: "All Resources", to: "/resources" }, { label: "Blog", to: "/blog" }, { label: "How NFC Works", to: "/how-nfc-works" }, { label: "Contact", to: "mailto:support@pipercard.com", external: true }] },
                { title: "Legal", links: [{ label: "Terms", to: "/terms" }, { label: "Privacy", to: "/privacy" }] }
              ].map((section, i) => (
                <div key={i}>
                  <h4 className="text-white font-semibold mb-2 sm:mb-4 text-sm sm:text-base">{section.title}</h4>
                  <ul className="space-y-1.5 sm:space-y-3 text-xs sm:text-sm">
                    {section.links.map((link, j) => (
                      <li key={j}>
                        {link.external ? (
                          <a href={link.to} className="hover:text-amber-400 transition-colors">{link.label}</a>
                        ) : (
                          <Link to={link.to} className="hover:text-amber-400 transition-colors">{link.label}</Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div className="border-t border-gray-800 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">© {new Date().getFullYear()} Piper Card. All rights reserved.</p>
              <div className="flex items-center gap-4 sm:gap-5">
                <a href="https://www.instagram.com/pipercard/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://twitter.com/pipercard" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-800 hover:bg-blue-500 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://linkedin.com/company/pipercard" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* Modals */}
        <SignInModal open={showSignInModal} onOpenChange={setShowSignInModal} />
        <BookDemoModal open={showDemoModal} onOpenChange={setShowDemoModal} />
      </div>
    </>
  );
};

export default LandingPage;
